"use client";
import React, { type JSX } from "react";
import { CardContent } from "../common/card";
import SideNotchCard from "../common/SideNotchCard";
import Image from "next/image";
import { Heading } from "@/components/ui/typography";

const steps = [
  {
    number: "1",
    title: "Free Strategy Call",
    description: (
      <p className="flex">
        We review your business model, customer geography, and payment flows to
        design the right structure before anything is filed.
      </p>
    ),
    icon: "/one-step.svg",
    alignment: "right", // Right 1
  },
  {
    number: "2",
    title: "Structure Plan Delivered",
    description: (
      <p className="flex items-start">
        Full Hong Kong company registration including Certificate of
        Incorporation and Business Registration Certificate.
      </p>
    ),
    icon: "/two-step.svg",
    alignment: "left", // Left 1
  },
  {
    number: "3",
    title: "Documents Submitted",
    description: (
      <>
        <p className="flex">
          We document your business operations, customer geography, contracts,
          and management location to build the offshore claim.
        </p>
      </>
    ),
    icon: "/three-step.svg",
    alignment: "right", // Right 2
  },
  {
    number: "4",
    title: "Certificate of Incorporation Issued",
    description: (
      <p className="flex items-start">
        We prepare and submit the full offshore profits exemption application to
        the Inland Revenue Department on your behalf.
      </p>
    ),
    icon: "/four-step.svg",
    alignment: "left", // Left 2
  },
  {
    number: "5",
    title: "Registered Address and Secretary Activated",
    description: (
      <div className="flex flex-col gap-2">
        <p className="flex">
          We manage any follow-up questions from the IRD throughout the review
          process.
        </p>
      </div>
    ),
    icon: "/five-step.svg",
    alignment: "right", // Right 3
  },
  {
    number: "6",
    title: "Bank Application Submitted",
    description: (
      <p className="flex items-start">
        Your company&apos;s offshore status is formally confirmed and becomes
        part of its official tax position.
      </p>
    ),
    icon: "/six-step.svg",
    alignment: "left", // Left 3
  },
  {
    number: "7",
    title: "Full Structure Operational and Offshore Positioning Prepared",
    description: (
      <p className="flex items-start">
        Ongoing bookkeeping and compliance to maintain your offshore status year
        after year.
      </p>
    ),
    icon: "/seven-step.svg",
    alignment: "right", // Right 4
  },
];
export const HowItFlowChart = ({ heading = "How the Offshore Profits Tax Exemption Application Works Step-by-Step" }: { heading?: string } = {}): JSX.Element => {
  return (
    <section className="relative w-full py-8 md:py-[60px] flex flex-col gap-[52px]">
      <div className="max-w-[1050px] mx-auto">
        <Heading as="h2" variant="sectionCentered">
          {heading}
        </Heading>
      </div>

      <div className="relative w-full max-w-[1440px] mx-auto px-4">
        <div className="relative min-h-[1400px] md:min-h-[1650px]">
          {/* Main Vertical Timeline Line */}
          <div className="absolute left-1/2 top-0 w-1 h-full bg-[linear-gradient(180deg,rgba(58,118,255,1)_0%,rgba(58,118,255,1)_70%,rgba(0,0,0,0)_100%)] -translate-x-1/2" />

          {steps.map((step, index) => {
            // Vertical offset spacing
            const topOffset = index * 250; // Controls the gap between steps

            return (
              <div
                key={index}
                className="absolute w-full"
                style={{ top: `${topOffset}px` }}
              >
                {/* Step Circle Number */}
                {/* <div className="absolute left-1/2 -translate-x-1/2 w-16 h-16 bg-white rounded-full overflow-hidden shadow-[0px_4px_4px_#004eff26] z-30">
                  <div className="absolute top-[60px] left-1/2 -translate-x-1/2 w-16 h-16 bg-[#004eff] rounded-full blur-[17px]" />
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 font-medium text-[#004eff] text-[36.6px] text-center leading-[37px]">
                    {step.number}
                  </div>
                </div> */}

                {/* Floating Icon */}
                <Image
                  className={`absolute w-[108px] h-[87px] z-40 sm:block hidden  ${
                    step.alignment === "right"
                      ? "left-[calc(50%+20px)]"
                      : "right-[calc(50%+20px)]"
                  }`}
                  width={108}
                  height={87}
                  alt="Icon"
                  src={step.icon}
                />

                {/* Notch Card */}
                <SideNotchCard
                  notch={{
                    width: { base: -20, sm: -20, md: 50, lg: 50 },
                    depth: { base: 0, sm: 0, md: 50, lg: 50 },
                    topLeft: 30,
                    topRight: 0,
                    bottomLeft: 20,
                    bottomRight: 20,
                  }}
                  widthClass="w-[530px]"
                  heightClass={step.alignment === "right" ? `${220}` : `${170}`}
                  borderColor="bg-blue-200"
                  shadowColor="#6a98ff"
                  borderWidth={2}
                  className={`absolute top-4 ${
                    step.alignment === "right"
                      ? "left-1/2 -translate-x-1/2 md:left-[calc(50%+63px)] md:-translate-x-0"
                      : "left-1/2 -translate-x-1/2 md:left-auto md:right-[calc(50%+63px)] md:-translate-x-0 transform scale-x-[-1]"
                  } border-0 shadow-none bg-transparent`}
                >
                  <CardContent
                    className={`relative flex flex-col gap-3 ${step.alignment === "left" && "scale-x-[-1]"}`}
                  >
                    <div
                      className={`absolute bg-[#E5EDFF] rounded-lg p-[10px] text-[#004EFF] text-[12px] ${step.alignment === "right" ? "top-2 right-4" : "top-2 right-16"} text-black font-[300]`}
                    >
                      COMPLETED
                    </div>
                    <div
                      className={`h-auto flex ${
                        step.alignment === "right"
                          ? " sm:ps-12 ps-0"
                          : "justify-start"
                      } font-medium text-white text-[20px] leading-[1.2] pt-4 whitespace-pre-wrap`}
                    >
                      {step.title}
                    </div>

                    <div className="flex w-full items-center gap-2.5 text-start">
                      <div className="relative w-fit font-normal text-white/70 text-base lg:text-[15px] leading-6">
                        {step.description}
                      </div>
                    </div>
                  </CardContent>
                </SideNotchCard>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
